import React from 'react';
import './GridBannerPlacement.css';
import { CmsMeta } from '../cmsMeta';

export type GridBannerPlacementProps = {
	// Desktop grid placement (1-based line numbers, width/height are number of tracks)
	desktopStartColumn: string;
	desktopWidth: string;
	desktopStartRow: string;
	desktopHeight: string;
	// Mobile grid placement (1-based line numbers, width/height are number of tracks)
	mobileStartColumn: string;
	mobileWidth: string;
	mobileStartRow: string;
	mobileHeight: string;
	children: React.ReactElement; // Expects exactly one child element
};

export const GridBannerPlacement = ({
	desktopStartColumn,
	desktopWidth,
	desktopStartRow,
	desktopHeight,
	mobileStartColumn,
	mobileWidth,
	mobileStartRow,
	mobileHeight,
	children,
}: GridBannerPlacementProps): JSX.Element => {
	// Convert string props from CMS into numbers for grid calculations
	const desktopStartColumnNum = Number(desktopStartColumn);
	const desktopWidthNum = Number(desktopWidth);
	const desktopStartRowNum = Number(desktopStartRow);
	const desktopHeightNum = Number(desktopHeight);

	const mobileStartColumnNum = Number(mobileStartColumn);
	const mobileWidthNum = Number(mobileWidth);
	const mobileStartRowNum = Number(mobileStartRow);
	const mobileHeightNum = Number(mobileHeight);

	const desktopColStart = desktopStartColumnNum;
	const desktopColEnd = desktopStartColumnNum + desktopWidthNum;
	const desktopRowStart = desktopStartRowNum;
	const desktopRowEnd = desktopStartRowNum + desktopHeightNum;

	const mobileColStart = mobileStartColumnNum;
	const mobileColEnd = mobileStartColumnNum + mobileWidthNum;
	const mobileRowStart = mobileStartRowNum;
	const mobileRowEnd = mobileStartRowNum + mobileHeightNum;

	// Wrap with a div that has the grid positioning
	return (
		<div
			className="grid-positioned"
			style={
				{
					'--desktop-col-start': desktopColStart,
					'--desktop-col-end': desktopColEnd,
					'--desktop-row-start': desktopRowStart,
					'--desktop-row-end': desktopRowEnd,
					'--mobile-col-start': mobileColStart,
					'--mobile-col-end': mobileColEnd,
					'--mobile-row-start': mobileRowStart,
					'--mobile-row-end': mobileRowEnd,
				} as React.CSSProperties
			}
		>
			{children}
		</div>
	);
};

export const cmsMeta: CmsMeta = {
	description:
		'A box placed among the products on a category or brand page, holding one child - almost always a ProductBoxBanner. It only goes in two kinds of file: categories/<perma>.json, where the whole file is this one placement, and brands/<slug>.json or brands/products-banners/<slug>.json, where any number of them may sit beside a BrandHeader. Publishing the file is all it takes: the page learns from the index of the market that it exists, within about a quarter of an hour. On a category page it is hidden as soon as a visitor filters or sorts; on a brand page it stays. Positions are grid lines counted in product boxes, 1-based, as strings: the start column and row, and a width and height in boxes. Desktop (from 1025px) has 4 columns; mobile has 2, and 3 on a tablet, so a mobile width of 2 fills the row. The mobile row is counted from the first product row - the page adds its own offset for what sits above the products.',
	example: [
		'GridBannerPlacement',
		{
			desktopStartColumn: '1',
			desktopStartRow: '3',
			desktopWidth: '2',
			desktopHeight: '1',
			mobileStartColumn: '1',
			mobileStartRow: '3',
			mobileWidth: '2',
			mobileHeight: '1',
		},
		[
			'ProductBoxBanner',
			{
				image: 'https://cdn.ondio.com/assets/141f1768a23848c4a6688499d813148e.jpg',
				logo: '',
				badgeText: 'Ergonomi hemma',
				html: '<h2>Sitter du bra när du jobbar hemma?</h2><p>Små justeringar kan göra stor skillnad. Upptäck Leitz ergonomiska produkter för bättre hållning, avlastning och komfort.</p>',
				ctaText: 'Hitta rätt stöd',
				ctaLink: '/kampanjer/leitz-ergonomi-hemma',
			},
		],
	],
};
